import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, TrendingDown, Minus, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';
import { GeoFilters } from '@/components/GeoFilters';

const formatINR = (v) => {
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v?.toLocaleString('en-IN') || '0';
};

const MONTH_NAMES = { '2025-11': 'Nov 2025', '2025-12': 'Dec 2025', '2026-01': 'Jan 2026', '2026-02': 'Feb 2026' };

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="text-xs font-heading font-semibold text-foreground mb-1">{MONTH_NAMES[label] || label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />
          {p.name}: {typeof p.value === 'number' ? (p.value > 1000 ? formatINR(p.value) : p.value.toLocaleString('en-IN')) : p.value}
        </p>
      ))}
    </div>
  );
}

function DeltaCard({ label, current, previous, format = 'number', suffix = '' }) {
  const change = current - previous;
  const changePct = previous !== 0 ? ((current - previous) / previous * 100) : 0;
  const isPositive = change > 0;
  const isNeutral = Math.abs(changePct) < 0.5;

  const displayValue = format === 'inr' ? `INR ${formatINR(current)}` : format === 'pct' ? `${current}%` : current?.toLocaleString('en-IN');
  const displayPrev = format === 'inr' ? `INR ${formatINR(previous)}` : format === 'pct' ? `${previous}%` : previous?.toLocaleString('en-IN');

  return (
    <div className="bg-card border border-border rounded-md p-4 hover:-translate-y-0.5 transition-transform duration-200" data-testid={`delta-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="kpi-label text-muted-foreground">{label}</p>
      <p className="text-xl font-heading font-bold mt-1">{displayValue}{suffix}</p>
      <div className="flex items-center gap-2 mt-2">
        {isNeutral ? (
          <span className="flex items-center gap-0.5 text-xs text-muted-foreground"><Minus size={12} /> No change</span>
        ) : isPositive ? (
          <span className="flex items-center gap-0.5 text-xs text-green-600 dark:text-green-400">
            <ArrowUpRight size={12} /> +{Math.abs(changePct).toFixed(1)}%
          </span>
        ) : (
          <span className="flex items-center gap-0.5 text-xs text-red-600 dark:text-red-400">
            <ArrowDownRight size={12} /> {changePct.toFixed(1)}%
          </span>
        )}
        <span className="text-xs text-muted-foreground">vs {displayPrev}</span>
      </div>
    </div>
  );
}

export default function ComparisonPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ months: [], deltas: [], sales_trend: [], category_monthly: {} });
  const [zone, setZone] = useState('');
  const [subzone, setSubzone] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');

  useEffect(() => {
  }, [api]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (zone) params.zone = zone;
        if (subzone) params.subzone = subzone;
        if (state) params.state = state;
        if (city) params.city = city;
        const res = await api.get('/comparison', { params });
        setData(res.data);
      } catch (err) {
        toast.error('Failed to load comparison data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, zone, subzone, state, city]);

  const latestDelta = data.deltas?.length > 0 ? data.deltas[data.deltas.length - 1] : null;
  const chartData = data.months?.map(m => ({
    ...m,
    month_label: MONTH_NAMES[m.month] || m.month
  }));

  // Category comparison data
  const catData = Object.entries(data.category_monthly || {}).map(([cat, months]) => ({
    category: cat,
    ...months
  })).sort((a, b) => {
    const aTotal = Object.values(data.category_monthly[a.category] || {}).reduce((s, v) => s + v, 0);
    const bTotal = Object.values(data.category_monthly[b.category] || {}).reduce((s, v) => s + v, 0);
    return bTotal - aTotal;
  }).slice(0, 8);

  const monthKeys = data.months?.map(m => m.month) || [];
  const COLORS = ['#1B3A8C', '#B8974A', '#10B981', '#F59E0B'];

  return (
    <div className="space-y-6" data-testid="comparison-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Period Comparison</h1>
          <p className="text-sm text-muted-foreground mt-1">Month-over-month performance analysis</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <GeoFilters api={api} zone={zone} setZone={setZone} subzone={subzone} setSubzone={setSubzone}
          state={state} setState={setState} city={city} setCity={setCity} />
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading comparison data...</div>
      ) : (
        <>
          {/* MoM Delta Cards */}
          {latestDelta && (
            <div>
              <h3 className="font-heading font-semibold text-sm mb-3 text-muted-foreground">
                {MONTH_NAMES[latestDelta.month] || latestDelta.month} vs {MONTH_NAMES[latestDelta.vs_month] || latestDelta.vs_month}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <DeltaCard label="Order Amount" current={latestDelta.order_amount_current} previous={latestDelta.order_amount_previous} format="inr" />
                <DeltaCard label="Fulfilled" current={latestDelta.fulfilled_amount_current} previous={latestDelta.fulfilled_amount_previous} format="inr" />
                <DeltaCard label="Fulfilment Rate" current={latestDelta.fulfilment_rate_current} previous={latestDelta.fulfilment_rate_previous} format="pct" />
                <DeltaCard label="Orders" current={latestDelta.order_count_current} previous={latestDelta.order_count_previous} />
                <DeltaCard label="Outlets" current={latestDelta.outlets_covered_current} previous={latestDelta.outlets_covered_previous} />
                <DeltaCard label="SKUs" current={latestDelta.skus_ordered_current} previous={latestDelta.skus_ordered_previous} />
              </div>
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Order Trend */}
            <div className="bg-card border border-border rounded-md p-5">
              <h3 className="font-heading font-semibold text-sm mb-4">Monthly Order Amount Trend</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={m => MONTH_NAMES[m]?.split(' ')[0] || m} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={formatINR} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="order_amount" name="Order Amount" fill="#1B3A8C" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="fulfilled_amount" name="Fulfilled" fill="#B8974A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Fulfilment Rate Trend */}
            <div className="bg-card border border-border rounded-md p-5">
              <h3 className="font-heading font-semibold text-sm mb-4">Fulfilment Rate & Outlet Coverage Trend</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={m => MONTH_NAMES[m]?.split(' ')[0] || m} stroke="hsl(var(--muted-foreground))" />
                    <YAxis yAxisId="left" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" domain={[90, 100]} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="left" type="monotone" dataKey="fulfilment_rate" name="Fulfilment %" stroke="#10B981" strokeWidth={2.5} dot={{ r: 5, fill: '#10B981' }} />
                    <Line yAxisId="right" type="monotone" dataKey="outlets_covered" name="Outlets" stroke="#6366F1" strokeWidth={2} dot={{ r: 4, fill: '#6366F1' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Category MoM */}
            <div className="bg-card border border-border rounded-md p-5 lg:col-span-2">
              <h3 className="font-heading font-semibold text-sm mb-4">Category Performance by Month</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={catData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="category" tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={formatINR} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {monthKeys.map((mk, i) => (
                      <Bar key={mk} dataKey={mk} name={MONTH_NAMES[mk] || mk} fill={COLORS[i % COLORS.length]} radius={[2, 2, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Monthly Summary Table */}
          <div className="bg-card border border-border rounded-md p-5 overflow-auto">
            <h3 className="font-heading font-semibold text-sm mb-4">Monthly Summary</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 text-xs font-heading text-muted-foreground">Month</th>
                  <th className="text-right py-2 px-3 text-xs font-heading text-muted-foreground">Order Amount</th>
                  <th className="text-right py-2 px-3 text-xs font-heading text-muted-foreground">Fulfilled</th>
                  <th className="text-right py-2 px-3 text-xs font-heading text-muted-foreground">Ful %</th>
                  <th className="text-right py-2 px-3 text-xs font-heading text-muted-foreground">Orders</th>
                  <th className="text-right py-2 px-3 text-xs font-heading text-muted-foreground">Outlets</th>
                  <th className="text-right py-2 px-3 text-xs font-heading text-muted-foreground">Avg Order</th>
                </tr>
              </thead>
              <tbody>
                {data.months?.map((m, i) => (
                  <tr key={m.month} className="border-b border-border/50 hover:bg-muted/30">
                    <td className="py-2.5 px-3 font-medium">{MONTH_NAMES[m.month] || m.month}</td>
                    <td className="text-right py-2.5 px-3">INR {formatINR(m.order_amount)}</td>
                    <td className="text-right py-2.5 px-3">INR {formatINR(m.fulfilled_amount)}</td>
                    <td className="text-right py-2.5 px-3">
                      <span className={m.fulfilment_rate >= 97 ? 'text-green-600 dark:text-green-400' : m.fulfilment_rate >= 95 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}>
                        {m.fulfilment_rate}%
                      </span>
                    </td>
                    <td className="text-right py-2.5 px-3">{m.order_count?.toLocaleString('en-IN')}</td>
                    <td className="text-right py-2.5 px-3">{m.outlets_covered?.toLocaleString('en-IN')}</td>
                    <td className="text-right py-2.5 px-3">INR {formatINR(m.avg_order_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
